import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  IconButton
} from '@mui/material';
import {
  Mic as PodcastsIcon,
  PlayArrow as PlayIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import BackButton from '../BackButton';

const PodcastsScreen = () => {
  const podcastData = {
    name: 'Sports Talk Podcast',
    image: '/assets/podcast.jfif',
    description: 'Two passionate sports fans turn their love for the game into conversation. Hosting a high-energy sports podcast, they break down the latest in football, basketball, and beyond—bringing insights, debates, and laughs straight from the mic to their audience.'
  };

  const episodes = [
    {
      id: 1,
      title: 'NFL Week 3 Predictions & Analysis',
      description: 'Breaking down the biggest matchups and upsets to watch this week',
      duration: '42:15',
      date: 'Sep 20, 2025'
    },
    {
      id: 2,
      title: 'NFL Week 2 Predictions & Analysis',
      description: 'Early season adjustments and week 2 betting opportunities',
      duration: '45:18',
      date: 'Sep 12, 2025'
    },
    {
      id: 3,
      title: 'NFL Week 1 Predictions',
      description: 'Season opener predictions and best bets for opening weekend',
      duration: '48:32',
      date: 'Sep 5, 2025'
    },
    {
      id: 4,
      title: 'College Football Chaos',
      description: 'Discussing the biggest upsets and what it means for playoffs',
      duration: '45:08',
      date: 'Aug 29, 2025'
    },
    {
      id: 5,
      title: 'MLB Playoff Race Heating Up',
      description: 'Which teams will make the wildcard and division predictions',
      duration: '39:17',
      date: 'Aug 22, 2025'
    },
    {
      id: 6,
      title: 'Summer Sports Recap',
      description: 'Reflecting on baseball season and looking ahead to football',
      duration: '47:25',
      date: 'Aug 15, 2025'
    }
  ];

  const handlePlayEpisode = (episode) => {
    // TODO: Navigate to episode player
    console.log(`Play episode: ${episode.title}`);
  };

  return (
    <Box sx={{ py: 2 }}>
      <BackButton />
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <PodcastsIcon sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }} />
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Podcasts
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Listen to sports betting podcasts, expert analysis, and betting tips
        </Typography>
      </Box>

      {/* Podcast Card */}
      <Grid container justifyContent="center">
        <Grid item xs={12} md={8} lg={6}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
              },
            }}
          >
            {/* Podcast Name at the top */}
            <Box sx={{ p: 2, pb: 0, textAlign: 'center' }}>
              <Typography variant="h5" component="h2" gutterBottom fontWeight="bold">
                {podcastData.name}
              </Typography>
            </Box>

            {/* Main content section with Image and Description side by side */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
              {/* Podcast Image */}
              <Box sx={{ 
                width: { xs: '100%', sm: '250px' },
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center'
              }}>
                <CardMedia
                  component="img"
                  height="250"
                  image={podcastData.image}
                  alt={`${podcastData.name} cover`}
                  sx={{
                    objectFit: 'cover',
                    backgroundColor: 'grey.100',
                    width: '100%',
                    borderRadius: 1
                  }}
                />
              </Box>
              
              {/* Description */}
              <Box sx={{ 
                flex: 1,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  About:
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    lineHeight: 1.6,
                    mb: 2
                  }}
                >
                  {podcastData.description}
                </Typography>
              </Box>
            </Box>

            <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
              {/* Episodes List */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Episodes
                </Typography>
              </Box>

              <Box
                sx={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: 'background.paper'
                }}
              >
                <List sx={{ p: 0 }}>
                  {episodes.map((episode, index) => (
                    <React.Fragment key={episode.id}>
                      <ListItem sx={{ p: 0 }}>
                        <ListItemButton
                          onClick={() => handlePlayEpisode(episode)}
                          sx={{
                            py: 2,
                            px: 2,
                            '&:hover': {
                              backgroundColor: 'action.hover'
                            }
                          }}
                        >
                          <IconButton
                            sx={{
                              mr: 2
                            }}
                            size="small"
                          >
                            <PlayIcon />
                          </IconButton>
                          
                          <ListItemText
                            primary={
                              <Typography variant="subtitle2" fontWeight="medium">
                                {episode.title}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                  {episode.description}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <TimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="text.secondary">
                                      {episode.duration}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {episode.date}
                                  </Typography>
                                </Box>
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                      {index < episodes.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PodcastsScreen;
